import { BackButton } from '../../components/BackButton';
import { Button } from '../../components/Button';
import { StepProgress } from '../../components/StepProgress';
import { StickyActionBar } from '../../components/StickyActionBar';
import { formatCurrency } from '../../utils/formatCurrency';
import { BookingNotesCard } from '../BookingNotesCard';
import { GuestDetailsPanel } from '../GuestDetailsPanel';
import {
  ActionBarArea,
  AddGuestRow,
  FooterLeft,
  FooterRow,
  FooterTitle,
  FooterTotal,
  FormGroup,
  Heading,
  HeadingAccent,
  Inner,
  ProgressSlot,
  ScrollArea,
  TopRow,
  Wrapper,
} from './GuestDetailsContentPanel.styles';
import type { GuestDetailsContentPanelProps } from './GuestDetailsContentPanel.types';

export function GuestDetailsContentPanel({
  guests,
  onGuestChange,
  onExistingGuestSelect,
  existingGuests,
  titleOptions,
  genderOptions,
  maxGuests = 2,
  onAddGuest,
  onRemoveGuest,
  showNotes = true,
  notesChecked = false,
  onNotesToggle,
  notesValue = '',
  onNotesChange,
  onBack,
  currentStep = 1,
  totalSteps,
  footerTitle,
  totalAmount,
  currencyCode,
  locale = 'en-GB',
  onNext,
  nextDisabled = false,
  labels = {},
}: GuestDetailsContentPanelProps) {
  const {
    title = 'Guest details.',
    titleAccent = 'Just a name to hold your room.',
    backAriaLabel = 'Go back',
    progressLabel = 'Booking progress',
    guestTitleFormatter,
    guestSubtitle,
    dividerText,
    titleLabel,
    titlePlaceholder,
    firstNameLabel,
    lastNameLabel,
    addGuest = 'Add guest',
    removeGuest = 'Remove',
    notesTitle,
    notesPlaceholder,
    totalLabel = 'Total',
    nextLabel = 'Next',
  } = labels;

  const canAddGuest = !!onAddGuest && guests.length < maxGuests;

  const selectedIdsByIndex = guests.map((guest) => guest.selectedExistingGuestId);

  return (
    <Wrapper>
      <ScrollArea>
        <Inner>
          {(onBack || totalSteps) && (
            <TopRow>
              {onBack && <BackButton onClick={onBack} ariaLabel={backAriaLabel} />}
              {!!totalSteps && (
                <ProgressSlot>
                  <StepProgress
                    totalSteps={totalSteps}
                    currentStep={currentStep}
                    label={progressLabel}
                  />
                </ProgressSlot>
              )}
            </TopRow>
          )}
          <Heading>
            {title} <HeadingAccent>{titleAccent}</HeadingAccent>
          </Heading>
          <FormGroup>
            {guests.map((guest, index) => (
              <GuestDetailsPanel
                key={index}
                guestNumber={index + 1}
                showHeader={index > 0}
                subtitle={guestSubtitle}
                onRemove={index > 0 && onRemoveGuest ? () => onRemoveGuest(index) : undefined}
                existingGuests={existingGuests}
                selectedGuestId={guest.selectedExistingGuestId}
                disabledGuestIds={selectedIdsByIndex.filter(
                  (id, otherIndex): id is string => !!id && otherIndex !== index
                )}
                onExistingGuestSelect={(guestId) => onExistingGuestSelect?.(index, guestId)}
                values={guest.values}
                errors={guest.errors}
                onChange={(field, value) => onGuestChange(index, field, value)}
                titleOptions={titleOptions}
                genderOptions={genderOptions}
                showGender={false}
                dividerText={dividerText}
                requiredFields={index === 0 ? { firstName: true } : {}}
                labels={{
                  guestTitleFormatter,
                  titleLabel,
                  titlePlaceholder,
                  firstNameLabel,
                  lastNameLabel,
                  removeLabel: removeGuest,
                }}
              />
            ))}
            {canAddGuest && (
              <AddGuestRow>
                <Button type="button" variant="secondary" size="small" onClick={onAddGuest}>
                  {addGuest}
                </Button>
              </AddGuestRow>
            )}
            {showNotes && (
              <BookingNotesCard
                checked={notesChecked}
                onToggle={(checked) => onNotesToggle?.(checked)}
                value={notesValue}
                onChange={(value) => onNotesChange?.(value)}
                labels={{ title: notesTitle, placeholder: notesPlaceholder }}
              />
            )}
          </FormGroup>
        </Inner>
      </ScrollArea>
      <ActionBarArea>
        <StickyActionBar>
          <FooterRow>
            <FooterLeft>
              {footerTitle && <FooterTitle>{footerTitle}</FooterTitle>}
              <FooterTotal>
                {totalLabel} {formatCurrency(totalAmount, currencyCode, { locale })}
              </FooterTotal>
            </FooterLeft>
            <Button type="button" variant="primary" onClick={onNext} disabled={nextDisabled}>
              {nextLabel}
            </Button>
          </FooterRow>
        </StickyActionBar>
      </ActionBarArea>
    </Wrapper>
  );
}
